import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminUserActivityListAction, adminUserActivityTypesAction } from "../Action/action";

export function formatActivityDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  }
  return String(value);
}

export function activityDeviceLabel(row) {
  if (!row) return "—";
  if (row.device_label) return row.device_label;
  const type = row.device_type ? String(row.device_type).trim() : "";
  const model = row.device_model ? String(row.device_model).trim() : "";
  const os = [row.os_name, row.os_version].filter((part) => part && String(part).trim()).join(" ").trim();
  const parts = [];
  if (model) parts.push(model);
  else if (type) parts.push(type);
  if (os) parts.push(os);
  return parts.length ? parts.join(" · ") : "—";
}

export function activityLabel(row) {
  if (!row) return "—";
  if (row.activity_label) return row.activity_label;
  if (!row.activity) return "—";
  return String(row.activity)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export default function UserActivity() {
  const [query, setQuery] = useState("");
  const [activity, setActivity] = useState("All");
  const [method, setMethod] = useState("All");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [types, setTypes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });

  useEffect(() => {
    let cancelled = false;
    async function loadTypes() {
      try {
        const res = await adminUserActivityTypesAction();
        if (cancelled) return;
        setTypes(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setTypes([]);
      }
    }
    loadTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [query, activity, method]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminUserActivityListAction({
          page,
          per_page: perPage,
          search: query.trim() || undefined,
          activity: activity === "All" ? undefined : activity,
          method: method === "All" ? undefined : method
        });
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setMeta({ total: 0, page, per_page: perPage });
          setError(res?.message || res?.msg || "Failed to load user activity.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load user activity.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, activity, method, reloadNonce]);

  const total = meta?.total ? Number(meta.total) : 0;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && rows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && rows.length ? start + rows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const clearAndReload = () => {
    setQuery("");
    setActivity("All");
    setMethod("All");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>User activity unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3" style={{ flexWrap: "wrap" }}>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search user, email, activity, endpoint…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>User name</th>
                <th>Activity</th>
                <th>Key</th>
                <th>Device</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((r, index) => (
                  <tr key={r.id != null ? r.id : `row-${index}`}>
                    <td>
                      {r.user_id ? (
                        <Link to={`/users/${r.user_id}`} className="action-link">
                          <strong>{r.user_name || r.user_email || `User #${r.user_id}`}</strong>
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <strong>{activityLabel(r)}</strong>
                    </td>
                    <td>{r.activity || "—"}</td>
                    <td>{activityDeviceLabel(r)}</td>
                    <td>{formatActivityDateTime(r.created_at)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state">
                      <div className="empty-icon">📜</div>
                      No user activity matches your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            Showing {start}-{end} of {total} Activities
          </span>
          <div className="pagination-pages">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              title="First page"
              aria-label="First page"
            >
              {"<<"}
            </button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
              ‹
            </button>
            {pageButtons.map((p) => (
              <button
                key={p}
                className={p === page ? "active" : ""}
                type="button"
                onClick={() => setPage(p)}
                disabled={loading}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
              aria-label="Last page"
            >
              {">>"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
